import type { EventListItem } from "@cdt/shared";
import { AlertTriangle, ChevronDown, ChevronUp, Loader2, RotateCcw } from "lucide-react";
import { useState } from "react";

import { StatusBadge } from "@/components/events/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useEventQuery } from "@/hooks/useEventQuery";
import { formatDateTime } from "@/lib/formatters";
import { cn } from "@/lib/utils";

interface ErrorsTableProps {
  items: EventListItem[];
  isFetching: boolean;
  retryingId: string | null;
  onRetry: (id: string) => void;
}

export function ErrorsTable({
  items,
  isFetching,
  retryingId,
  onRetry,
}: ErrorsTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-600 ring-1 ring-red-100">
            <AlertTriangle className="h-4 w-4" />
          </span>
          Errori con retry
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "overflow-hidden rounded-xl border border-slate-100",
            isFetching && "opacity-60",
          )}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Ricevuta</TableHead>
                <TableHead>Oggetto</TableHead>
                <TableHead className="w-[200px]">Mittente</TableHead>
                <TableHead className="w-[120px]">Stato</TableHead>
                <TableHead className="w-[170px] text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <ErrorRow
                  key={item.id}
                  item={item}
                  expanded={expandedId === item.id}
                  onToggle={() =>
                    setExpandedId((current) => (current === item.id ? null : item.id))
                  }
                  retrying={retryingId === item.id}
                  onRetry={() => onRetry(item.id)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

interface ErrorRowProps {
  item: EventListItem;
  expanded: boolean;
  onToggle: () => void;
  retrying: boolean;
  onRetry: () => void;
}

function ErrorRow({
  item,
  expanded,
  onToggle,
  retrying,
  onRetry,
}: ErrorRowProps) {
  const detailQuery = useEventQuery(expanded ? item.id : undefined);
  return (
    <>
      <TableRow className="hover:bg-transparent">
        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDateTime(item.emailReceivedAt)}
        </TableCell>
        <TableCell className="max-w-[360px] truncate" title={item.emailSubject}>
          {item.emailSubject}
        </TableCell>
        <TableCell className="truncate" title={item.emailFrom}>
          {item.emailFrom}
        </TableCell>
        <TableCell>
          <StatusBadge status={item.status} />
        </TableCell>
        <TableCell>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" size="sm" variant="outline" onClick={onToggle}>
              {expanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" />
                  Nascondi
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" />
                  Dettagli
                </>
              )}
            </Button>
            <Button type="button" size="sm" onClick={onRetry} disabled={retrying}>
              {retrying ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" />
              )}
              Retry
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={5} className="bg-muted/30">
            {detailQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Carico dettaglio...</p>
            ) : detailQuery.isError ? (
              <p className="text-sm text-destructive">
                Errore dettaglio:{" "}
                {detailQuery.error instanceof Error
                  ? detailQuery.error.message
                  : "Impossibile caricare"}
              </p>
            ) : detailQuery.data ? (
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Error message
                  </p>
                  <p className="mt-1 break-words">{detailQuery.data.errorMessage ?? "-"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Retry count
                  </p>
                  <p className="mt-1 tabular-nums">{detailQuery.data.retryCount}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nessun dettaglio disponibile.</p>
            )}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
